import { ActionIcon, Badge, Button, Stack, Tooltip } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { ArrowCounterClockwiseIcon, PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { DataGridBase, useDataGridBaseParams } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Truck, useListTrucksInfiniteQuery } from "@/store";

export const Route = createFileRoute("/_private/dashboard/_trucks/caminhoes/")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Lista de Caminhões" }] })
});

function RouteComponent() {
  const [query] = useDataGridBaseParams();
  const { id } = useSession();

  const { data, isFetching, hasNextPage, fetchNextPage, refetch } = useListTrucksInfiniteQuery(
    {
      id,
      params: {
        filter: query?.quickFilterValues?.join(" ") ?? "",
        pageSize: 50
      }
    },
    { skip: !id }
  );

  const rows = data?.pages.flatMap((page) => page.data) || [];
  const navigate = useNavigate();
  const columns: GridColDef<Truck>[] = [
    {
      field: "id",
      headerName: "Proprietário",
      display: "flex",
      minWidth: 250,
      type: "custom",
      flex: 1,
      renderCell: (params) => {
        const { row } = params;
        const { owner, driver } = row;

        return (
          <div>
            <p>
              <strong>Código:</strong> {params.value}
            </p>
            <p>{owner || driver?.name}</p>
          </div>
        );
      }
    },
    {
      field: "licensePlate",
      headerName: "Veículo",
      display: "flex",
      minWidth: 200,
      flex: 1,
      type: "custom",
      renderCell: (params) => {
        const { row } = params;
        return (
          <div>
            <p>
              <strong>Placa:</strong> {params.value}
            </p>
            <p>
              <strong>Renavam:</strong> {row.renavam}
            </p>
            {/*TODO Modelo não é retornado pela api*/}
            {/* <p>
              <strong>Modelo:</strong> {row.model} - {row.manufacturingYear}/{row.modelYear}
            </p> */}
          </div>
        );
      }
    },
    {
      field: "capacidades",
      headerName: "Capacidades",
      display: "flex",
      minWidth: 200,
      flex: 1,
      type: "custom",
      renderCell: (params) => {
        const { row } = params;
        return (
          <div>
            <p>
              <strong>Qtd de eixo:</strong> {row.shaftQuantity}
            </p>
            <p>
              <strong>Capac. bruta:</strong> {row.grossCapacity}t
            </p>
            <p>
              <strong>Capac. líquida:</strong> {row.netCapacity}t
            </p>
          </div>
        );
      }
    },
    {
      field: "implementType",
      headerName: "Tipo de implemento",
      minWidth: 150,
      flex: 1,
      type: "string"
    },
    {
      field: "isEnabled",
      headerName: "Status",
      type: "boolean",
      renderCell: (params) => {
        return (
          <Badge color={params.value ? "green" : "red"} size="lg" variant="light">
            {params.value ? "Ativo" : "Inativo"}
          </Badge>
        );
      }
    },
    {
      field: "shippingCompany",
      headerName: "Transportadora",
      minWidth: 200,
      type: "string",
      valueGetter: (params, { shippingCompany }) => shippingCompany?.name
    },
    {
      field: "company",
      headerName: "Empresa",
      minWidth: 150,
      type: "string",
      valueGetter: (params, { company }) => company.name
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      renderCell: ({ row }) => (
        <ActionIcon.Group>
          <Tooltip label="Editar Caminhão">
            <ActionIcon variant="light" color="yellow" onClick={() => navigate({ to: "/dashboard/caminhoes/editar/$id", params: { id: row.id } })}>
              <PencilIcon />
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Excluir Caminhão">
            <ActionIcon variant="light" color="red" onClick={() => handleDelete(row.id)}>
              <TrashIcon />
            </ActionIcon>
          </Tooltip>
        </ActionIcon.Group>
      )
    }
  ];

  const handleDelete = (id: number) => {
    MODAL_HANDLER.delete.truck({ id });
  };

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <DashboardComponents.Title>Lista de Caminhões</DashboardComponents.Title>

        <div className="flex flex-1 items-center justify-end gap-2">
          <ActionIcon onClick={() => refetch()} loading={isFetching} size="xl">
            <ArrowCounterClockwiseIcon />
          </ActionIcon>

          <Button onClick={fetchNextPage} disabled={!hasNextPage || isFetching} className="max-md:flex-1!">
            Carregar Mais
          </Button>

          <Tooltip label="Cadastrar Caminhão">
            <ActionIcon size="xl" onClick={() => navigate({ to: "/dashboard/caminhoes/cadastrar" })}>
              <PlusIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      <DataGridBase rows={rows} columns={columns} loading={isFetching} />
    </Stack>
  );
}
